import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Input, Spinner } from '@components/ui';
import { useThemeStore, useProjectStore } from '@store';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useProfile,
  usePreferences,
  useUpdatePreferences,
  useTaskStatuses,
  useCreateTaskStatus,
  useUpdateTaskStatusConfig,
  useDeleteTaskStatus,
  useReorderTaskStatuses,
  useClients,
} from '@hooks';
import TransitionRulesEditor from '@components/features/TransitionRulesEditor';
import RolesSection from '@components/features/RolesSection';
import MembersSection from '@components/features/MembersSection';
import PortalAccessCredentials from '@components/features/PortalAccessCredentials';
import { usePermission } from '@hooks';

// Helper functions for role display
const getRoleLabel = (role) => {
  const roles = {
    ADMIN: 'Administrador',
    MANAGER: 'Manager',
    DEVELOPER: 'Desarrollador',
    CLIENT: 'Cliente',
  };
  return roles[role?.toUpperCase()] || role || 'Usuario';
};

const getRoleStyle = (role) => {
  const styles = {
    ADMIN: { bg: '#667eea15', color: '#667eea' },
    MANAGER: { bg: '#8b5cf615', color: '#8b5cf6' },
    DEVELOPER: { bg: '#10b98115', color: '#10b981' },
    CLIENT: { bg: '#f59e0b15', color: '#f59e0b' },
  };
  return styles[role?.toUpperCase()] || { bg: '#64748b15', color: '#64748b' };
};

// Profile Section
function ProfileSection() {
  const { data: profile, isLoading } = useProfile();

  if (isLoading) {
    return (
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner />
        </div>
      </Card>
    );
  }

  const sectionStyle = {
    marginBottom: '24px',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '8px',
  };

  return (
    <Card padding="lg">
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
        Perfil de Usuario
      </h2>

      <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
        {/* Avatar */}
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: profile?.avatar
                ? `url(${profile.avatar}) center/cover`
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              color: 'white',
              fontWeight: '600',
              border: '4px solid #e2e8f0',
            }}
          >
            {!profile?.avatar && (profile?.name?.charAt(0).toUpperCase() || 'U')}
          </div>
          <p style={{ marginTop: '8px', fontSize: '12px', color: '#94a3b8' }}>
            Foto de Microsoft
          </p>
        </div>

        {/* Profile Info */}
        <div style={{ flex: 1 }}>
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, color: '#64748b' }}>Nombre</label>
            <p style={{ fontSize: '16px', fontWeight: '500' }}>{profile?.name || '-'}</p>
          </div>
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, color: '#64748b' }}>Email</label>
            <p style={{ fontSize: '16px' }}>{profile?.email || '-'}</p>
          </div>
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, color: '#64748b' }}>Rol</label>
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: '500',
                borderRadius: '20px',
                background: getRoleStyle(profile?.role).bg,
                color: getRoleStyle(profile?.role).color,
              }}
            >
              {getRoleLabel(profile?.role)}
            </span>
          </div>
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, color: '#64748b' }}>Capacidad semanal</label>
            <p style={{ fontSize: '16px' }}>{profile?.capacity || 40} horas/semana</p>
          </div>
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, color: '#64748b' }}>Miembro desde</label>
            <p style={{ fontSize: '14px', color: '#64748b' }}>
              {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : '-'}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

// Theme Section
function ThemeSection() {
  const { theme, setTheme } = useThemeStore();

  const themes = [
    {
      id: 'light',
      name: 'Claro',
      icon: '☀️',
      preview: { bg: '#ffffff', sidebar: '#f8fafc', accent: '#667eea' },
    },
    {
      id: 'dark',
      name: 'Oscuro',
      icon: '🌙',
      preview: { bg: '#1e293b', sidebar: '#0f172a', accent: '#818cf8' },
    },
  ];

  return (
    <Card padding="lg">
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
        Tema de la Aplicación
      </h2>

      <div style={{ display: 'flex', gap: '16px' }}>
        {themes.map((t) => (
          <div
            key={t.id}
            onClick={() => setTheme(t.id)}
            style={{
              flex: 1,
              maxWidth: '200px',
              padding: '16px',
              borderRadius: '16px',
              border: `2px solid ${theme === t.id ? '#667eea' : '#e2e8f0'}`,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {/* Theme preview */}
            <div
              style={{
                height: '100px',
                borderRadius: '8px',
                marginBottom: '12px',
                background: t.preview.bg,
                display: 'flex',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
              }}
            >
              <div style={{ width: '30%', background: t.preview.sidebar }} />
              <div style={{ flex: 1, padding: '8px' }}>
                <div
                  style={{
                    width: '60%',
                    height: '8px',
                    background: t.preview.accent,
                    borderRadius: '4px',
                    marginBottom: '6px',
                  }}
                />
                <div
                  style={{
                    width: '80%',
                    height: '6px',
                    background: t.id === 'dark' ? '#334155' : '#e2e8f0',
                    borderRadius: '3px',
                    marginBottom: '4px',
                  }}
                />
                <div
                  style={{
                    width: '50%',
                    height: '6px',
                    background: t.id === 'dark' ? '#334155' : '#e2e8f0',
                    borderRadius: '3px',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                {t.icon} {t.name}
              </span>
              {theme === t.id && (
                <span
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#667eea',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                  }}
                >
                  ✓
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// Notifications Section
function NotificationsSection() {
  const { data: prefsData, isLoading } = usePreferences();
  const updatePreferences = useUpdatePreferences();

  const preferences = prefsData?.data;

  const handleToggle = async (key) => {
    try {
      await updatePreferences.mutateAsync({
        [key]: !preferences?.[key],
      });
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  if (isLoading) {
    return (
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner />
        </div>
      </Card>
    );
  }

  const ToggleSwitch = ({ checked, onChange }) => (
    <button
      onClick={onChange}
      style={{
        width: '48px',
        height: '26px',
        borderRadius: '13px',
        border: 'none',
        background: checked ? '#667eea' : '#cbd5e1',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 0.2s',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '3px',
          left: checked ? '25px' : '3px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: 'white',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );

  const settingRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid #e2e8f0',
  };

  return (
    <Card padding="lg">
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px' }}>
        Notificaciones
      </h2>

      <div>
        <div style={settingRowStyle}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
              Notificaciones por email
            </p>
            <p style={{ fontSize: '13px', color: '#64748b' }}>
              Recibe actualizaciones de tus proyectos por email
            </p>
          </div>
          <ToggleSwitch
            checked={preferences?.emailNotifications ?? true}
            onChange={() => handleToggle('emailNotifications')}
          />
        </div>

        <div style={{ ...settingRowStyle, borderBottom: 'none' }}>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
              Notificaciones push
            </p>
            <p style={{ fontSize: '13px', color: '#64748b' }}>
              Recibe notificaciones en tiempo real en el navegador
            </p>
          </div>
          <ToggleSwitch
            checked={preferences?.pushNotifications ?? true}
            onChange={() => handleToggle('pushNotifications')}
          />
        </div>
      </div>
    </Card>
  );
}

// Sortable Status Item for drag-and-drop
function SortableStatusItem({ status, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderRadius: '10px',
        background: status.bgColor,
        border: '1px solid #e2e8f0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          {...attributes}
          {...listeners}
          style={{
            cursor: 'grab',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            color: '#94a3b8',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="5" cy="3" r="1.5" />
            <circle cx="11" cy="3" r="1.5" />
            <circle cx="5" cy="8" r="1.5" />
            <circle cx="11" cy="8" r="1.5" />
            <circle cx="5" cy="13" r="1.5" />
            <circle cx="11" cy="13" r="1.5" />
          </svg>
        </div>
        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: status.color }} />
        <span style={{ fontWeight: '500', color: status.color }}>{status.label}</span>
        <span style={{ fontSize: '12px', color: '#94a3b8' }}>({status.name})</span>
        {status.clientVisible && (
          <span
            style={{
              fontSize: '11px',
              fontWeight: '500',
              padding: '2px 8px',
              borderRadius: '9999px',
              background: 'white',
              border: '1px solid #c7d2fe',
              color: '#4f46e5',
            }}
          >
            Visible al cliente
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => onEdit(status)}
          style={{
            padding: '6px 12px',
            fontSize: '13px',
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Editar
        </button>
        <button
          onClick={() => onDelete(status.id)}
          style={{
            padding: '6px 12px',
            fontSize: '13px',
            background: 'white',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            cursor: 'pointer',
            color: '#ef4444',
          }}
        >
          Eliminar
        </button>
      </div>
    </div>
  );
}

SortableStatusItem.propTypes = {
  status: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    bgColor: PropTypes.string.isRequired,
    clientVisible: PropTypes.bool,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// Kanban Statuses Section
function KanbanStatusesSection() {
  const { selectedProject } = useProjectStore();
  const projectId = selectedProject?.id;

  const { data: statusesData, isLoading } = useTaskStatuses(projectId);
  const createStatus = useCreateTaskStatus();
  const updateStatus = useUpdateTaskStatusConfig();
  const deleteStatus = useDeleteTaskStatus();
  const reorderStatuses = useReorderTaskStatuses();

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    color: '#64748b',
    bgColor: '#f1f5f9',
    clientVisible: false,
  });

  const statuses = statusesData?.data || [];

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({ name: '', label: '', color: '#64748b', bgColor: '#f1f5f9', clientVisible: false });
  };

  const handleEdit = (status) => {
    setEditingId(status.id);
    setIsAdding(false);
    setFormData({
      name: status.name,
      label: status.label,
      color: status.color,
      bgColor: status.bgColor,
      clientVisible: Boolean(status.clientVisible),
    });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: '', label: '', color: '#64748b', bgColor: '#f1f5f9', clientVisible: false });
  };

  const handleSave = async () => {
    if (!formData.label.trim()) return;

    try {
      if (editingId) {
        await updateStatus.mutateAsync({
          id: editingId,
          data: {
            label: formData.label,
            color: formData.color,
            bgColor: formData.bgColor,
            clientVisible: formData.clientVisible,
          },
        });
      } else {
        await createStatus.mutateAsync({
          projectId,
          name: formData.label.toLowerCase().replace(/\s+/g, '_'),
          label: formData.label,
          color: formData.color,
          bgColor: formData.bgColor,
          clientVisible: formData.clientVisible,
        });
      }
      handleCancel();
    } catch (error) {
      console.error('Error saving status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este estado?')) return;
    try {
      await deleteStatus.mutateAsync(id);
    } catch (error) {
      alert(error.response?.data?.message || 'Error al eliminar');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = statuses.findIndex((s) => s.id === active.id);
      const newIndex = statuses.findIndex((s) => s.id === over.id);
      const newOrder = arrayMove(statuses, oldIndex, newIndex);

      await reorderStatuses.mutateAsync({
        projectId,
        statusIds: newOrder.map((s) => s.id),
      });
    }
  };

  if (!projectId) {
    return (
      <Card padding="lg">
        <p style={{ color: '#64748b', textAlign: 'center' }}>
          Selecciona un proyecto para configurar sus estados
        </p>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner />
        </div>
      </Card>
    );
  }

  const colorPresets = [
    { color: '#64748b', bg: '#f1f5f9' },
    { color: '#3b82f6', bg: '#dbeafe' },
    { color: '#f59e0b', bg: '#fef3c7' },
    { color: '#8b5cf6', bg: '#ede9fe' },
    { color: '#10b981', bg: '#d1fae5' },
    { color: '#ef4444', bg: '#fee2e2' },
    { color: '#ec4899', bg: '#fce7f3' },
    { color: '#06b6d4', bg: '#cffafe' },
  ];

  return (
    <Card padding="lg">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
            Estados del Kanban
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b' }}>
            Proyecto: {selectedProject?.name}
          </p>
        </div>
        <Button variant="primary" onClick={handleAdd} disabled={isAdding}>
          + Agregar Estado
        </Button>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingId) && (
        <div
          style={{
            padding: '16px',
            marginBottom: '16px',
            borderRadius: '12px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Nombre del estado
              </label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Ej: En QA"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Color
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {colorPresets.map((preset, i) => (
                  <button
                    key={i}
                    onClick={() => setFormData({ ...formData, color: preset.color, bgColor: preset.bg })}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: preset.bg,
                      border: formData.color === preset.color ? `2px solid ${preset.color}` : '2px solid transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ width: '16px', height: '16px', borderRadius: '4px', background: preset.color }} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Client portal visibility */}
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              marginBottom: '16px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={formData.clientVisible}
              onChange={(e) => setFormData({ ...formData, clientVisible: e.target.checked })}
              style={{ marginTop: '3px' }}
            />
            <span>
              <span style={{ display: 'block', fontSize: '13px', fontWeight: '500' }}>
                Visible al cliente
              </span>
              <span style={{ display: 'block', fontSize: '12px', color: '#64748b' }}>
                Las tareas en este estado se muestran en el portal del cliente.
              </span>
            </span>
          </label>

          {/* Preview */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
              Vista previa
            </label>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '8px',
                background: formData.bgColor,
              }}
            >
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: formData.color }} />
              <span style={{ color: formData.color, fontWeight: '500', fontSize: '14px' }}>
                {formData.label || 'Nombre del estado'}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={createStatus.isPending || updateStatus.isPending}
            >
              {editingId ? 'Guardar' : 'Agregar'}
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Status List with Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={statuses.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {statuses.map((status) => (
              <SortableStatusItem
                key={status.id}
                status={status}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}

            {statuses.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                No hay estados configurados. Agrega el primero.
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </Card>
  );
}

// Client Access Section
function ClientAccessSection() {
  const { data: clientsData, isLoading, refetch } = useClients();
  const [activeTab, setActiveTab] = useState('clients'); // 'clients' o 'access'
  // Link and PIN of the access just generated: the only time they can be shown
  const [freshAccess, setFreshAccess] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', company: '', email: '', phone: '' });
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', company: '', email: '', phone: '' });

  const clients = clientsData?.data || [];

  const handleCreateClient = async () => {
    if (!createForm.name.trim()) {
      setError('El nombre es requerido');
      return;
    }
    if (!createForm.company.trim()) {
      setError('La empresa es requerida');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const { clientService } = await import('@services/clientService');
      await clientService.create(createForm);
      refetch();
      setIsCreating(false);
      setCreateForm({ name: '', company: '', email: '', phone: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear cliente');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({ name: '', company: '', email: '', phone: '' });
    setError('');
  };

  const handleEditClient = (client) => {
    setEditingClient(client.id);
    setEditForm({
      name: client.name || '',
      company: client.company || '',
      email: client.email || '',
      phone: client.phone || '',
    });
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingClient(null);
    setEditForm({ name: '', company: '', email: '', phone: '' });
    setError('');
  };

  const handleSaveClient = async () => {
    if (!editForm.name.trim()) {
      setError('El nombre es requerido');
      return;
    }
    if (!editForm.company.trim()) {
      setError('La empresa es requerida');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const { clientService } = await import('@services/clientService');
      await clientService.update(editingClient, editForm);
      refetch();
      handleCancelEdit();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar cliente');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`¿Estás seguro de eliminar al cliente "${client.name}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    setDeletingId(client.id);
    try {
      const { clientService } = await import('@services/clientService');
      await clientService.delete(client.id);
      refetch();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar cliente');
    } finally {
      setDeletingId(null);
    }
  };

  const handleGenerateToken = async (client) => {
    if (
      client.hasPortalAccess &&
      !window.confirm(`¿Regenerar acceso para ${client.name}? El enlace y el PIN actuales dejarán de funcionar.`)
    ) {
      return;
    }
    setGeneratingId(client.id);
    try {
      const { clientService } = await import('@services/clientService');
      const response = await clientService.generateToken(client.id);
      setFreshAccess({
        clientId: client.id,
        accessToken: response.data.accessToken,
        accessPin: response.data.accessPin,
      });
      refetch();
    } catch (error) {
      console.error('Error generating token:', error);
    } finally {
      setGeneratingId(null);
    }
  };

  const handleRevokeToken = async (client) => {
    if (!window.confirm(`¿Revocar acceso para ${client.name}?`)) return;
    setRevokingId(client.id);
    try {
      const { clientService } = await import('@services/clientService');
      await clientService.revokeToken(client.id);
      setFreshAccess((current) => (current?.clientId === client.id ? null : current));
      refetch();
    } catch (error) {
      console.error('Error revoking token:', error);
    } finally {
      setRevokingId(null);
    }
  };

  if (isLoading) {
    return (
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner />
        </div>
      </Card>
    );
  }

  // Estilos para tabs internos
  const innerTabsStyle = {
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
    background: '#f1f5f9',
    padding: '4px',
    borderRadius: '10px',
  };

  const innerTabStyle = (isActive) => ({
    flex: 1,
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '500',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    background: isActive ? 'white' : 'transparent',
    color: isActive ? '#1e293b' : '#64748b',
    boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  return (
    <Card padding="lg">
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
          Gestión de Clientes
        </h2>
        <p style={{ fontSize: '13px', color: '#64748b' }}>
          Administra tus clientes y su acceso al portal de proyectos
        </p>
      </div>

      {/* Tabs internos */}
      <div style={innerTabsStyle}>
        <button
          onClick={() => setActiveTab('clients')}
          style={innerTabStyle(activeTab === 'clients')}
        >
          <span>👥</span> Clientes
        </button>
        <button
          onClick={() => setActiveTab('access')}
          style={innerTabStyle(activeTab === 'access')}
        >
          <span>🔑</span> Control de Acceso
        </button>
      </div>

      {/* Tab: Clientes */}
      {activeTab === 'clients' && (
        <div>
          {/* Botón crear nuevo cliente */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            {!isCreating && (
              <Button variant="primary" onClick={() => setIsCreating(true)}>
                + Nuevo Cliente
              </Button>
            )}
          </div>

          {/* Formulario de crear cliente */}
          {isCreating && (
            <div
              style={{
                padding: '20px',
                marginBottom: '16px',
                borderRadius: '12px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}
            >
              <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
                Nuevo Cliente
              </h4>
              {error && (
                <div style={{
                  padding: '10px 14px',
                  marginBottom: '14px',
                  borderRadius: '8px',
                  background: '#fee2e2',
                  color: '#dc2626',
                  fontSize: '13px',
                }}>
                  {error}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' }}>
                    Nombre *
                  </label>
                  <Input
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Nombre del cliente"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' }}>
                    Empresa *
                  </label>
                  <Input
                    value={createForm.company}
                    onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                    placeholder="Nombre de la empresa"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' }}>
                    Email
                  </label>
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="email@ejemplo.com"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' }}>
                    Teléfono
                  </label>
                  <Input
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                    placeholder="+34 600 000 000"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <Button variant="secondary" onClick={handleCancelCreate}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleCreateClient} loading={isSaving}>
                  Crear Cliente
                </Button>
              </div>
            </div>
          )}

          {/* Lista de clientes */}
          {clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>👥</div>
              <p>No hay clientes registrados.</p>
              <p style={{ fontSize: '13px', marginTop: '8px' }}>Crea tu primer cliente usando el botón de arriba.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {clients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                          {client.name}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#64748b' }}>
                        {client.company}
                      </p>
                      {client.email && (
                        <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                          {client.email}
                        </p>
                      )}
                      {client.phone && (
                        <p style={{ fontSize: '12px', color: '#94a3b8' }}>
                          {client.phone}
                        </p>
                      )}
                      {client.projectCount > 0 && (
                        <p style={{ fontSize: '12px', color: '#667eea', marginTop: '6px', fontWeight: '500' }}>
                          {client.projectCount} proyecto{client.projectCount > 1 ? 's' : ''} asociado{client.projectCount > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEditClient(client)}
                        disabled={editingClient === client.id}
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '500',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: '#475569',
                        }}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client)}
                        disabled={deletingId === client.id}
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '500',
                          background: 'white',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: '#ef4444',
                          opacity: deletingId === client.id ? 0.6 : 1,
                        }}
                      >
                        {deletingId === client.id ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </div>
                  </div>

                  {/* Formulario de edición inline */}
                  {editingClient === client.id && (
                    <div
                      style={{
                        padding: '16px',
                        marginTop: '12px',
                        borderRadius: '8px',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                        Editar Cliente
                      </h4>
                      {error && (
                        <div style={{
                          padding: '8px 12px',
                          marginBottom: '12px',
                          borderRadius: '6px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          fontSize: '13px',
                        }}>
                          {error}
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '4px' }}>
                            Nombre *
                          </label>
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            placeholder="Nombre del cliente"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '4px' }}>
                            Empresa *
                          </label>
                          <Input
                            value={editForm.company}
                            onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                            placeholder="Nombre de la empresa"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '4px' }}>
                            Email
                          </label>
                          <Input
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            placeholder="email@ejemplo.com"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '4px' }}>
                            Teléfono
                          </label>
                          <Input
                            value={editForm.phone}
                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            placeholder="+34 600 000 000"
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button variant="secondary" onClick={handleCancelEdit}>
                          Cancelar
                        </Button>
                        <Button variant="primary" onClick={handleSaveClient} loading={isSaving}>
                          Guardar cambios
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Control de Acceso */}
      {activeTab === 'access' && (
        <div>
          {clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔑</div>
              <p>No hay clientes registrados.</p>
              <p style={{ fontSize: '13px', marginTop: '8px' }}>Crea un cliente primero en la pestaña "Clientes".</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {clients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                          {client.name}
                        </span>
                        <span
                          style={{
                            padding: '2px 8px',
                            fontSize: '11px',
                            fontWeight: '500',
                            borderRadius: '10px',
                            background: client.hasPortalAccess ? '#dcfce7' : '#fee2e2',
                            color: client.hasPortalAccess ? '#16a34a' : '#dc2626',
                          }}
                        >
                          {client.hasPortalAccess ? 'Acceso activo' : 'Sin acceso'}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#64748b' }}>
                        {client.company}
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      {client.hasPortalAccess && (
                        <button
                          onClick={() => handleRevokeToken(client)}
                          disabled={revokingId === client.id}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '500',
                            background: 'white',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#ef4444',
                            opacity: revokingId === client.id ? 0.6 : 1,
                          }}
                        >
                          {revokingId === client.id ? 'Revocando...' : 'Revocar acceso'}
                        </button>
                      )}
                      <button
                        onClick={() => handleGenerateToken(client)}
                        disabled={generatingId === client.id}
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '500',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: 'white',
                          opacity: generatingId === client.id ? 0.6 : 1,
                        }}
                      >
                        {generatingId === client.id
                          ? 'Generando...'
                          : client.hasPortalAccess
                            ? 'Regenerar acceso'
                            : 'Generar acceso'}
                      </button>
                    </div>
                  </div>

              {freshAccess?.clientId === client.id ? (
                <PortalAccessCredentials
                  accessToken={freshAccess.accessToken}
                  accessPin={freshAccess.accessPin}
                />
              ) : (
                client.hasPortalAccess && (
                  <p style={{ fontSize: '12px', color: '#64748b' }}>
                    El enlace y el PIN solo se muestran al generarlos. Si el cliente los perdió, usa
                    &quot;Regenerar acceso&quot;.
                  </p>
                )
              )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// Main Settings Page
export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('profile');

  const { hasPermission } = usePermission();

  // Tabs con permisos requeridos
  const allTabs = [
    { id: 'profile', label: 'Perfil', icon: '👤', permissions: [] },
    { id: 'appearance', label: 'Apariencia', icon: '🎨', permissions: [] },
    { id: 'notifications', label: 'Notificaciones', icon: '🔔', permissions: [] },
    { id: 'kanban', label: 'Kanban', icon: '📋', permissions: ['configuracion.editar_estados'] },
    { id: 'transitions', label: 'Transiciones', icon: '🔄', permissions: ['configuracion.editar_transiciones'] },
    { id: 'clients', label: 'Acceso Clientes', icon: '🔑', permissions: ['clientes.ver'] },
    { id: 'members', label: 'Miembros', icon: '👥', permissions: ['admin.gestionar_roles'] },
    { id: 'roles', label: 'Roles', icon: '👑', permissions: ['admin.gestionar_roles'] },
  ];

  // Filtrar tabs según permisos
  const tabs = allTabs.filter((tab) => {
    if (!tab.permissions || tab.permissions.length === 0) return true;
    return tab.permissions.some((p) => hasPermission(p));
  });

  const containerStyle = {
    padding: '24px',
    maxWidth: '1000px',
    margin: '0 auto',
  };

  const headerStyle = {
    marginBottom: '32px',
  };

  const tabsStyle = {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '1px solid #e2e8f0',
    paddingBottom: '16px',
  };

  const tabStyle = (isActive) => ({
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: isActive ? '#667eea' : '#64748b',
    background: isActive ? '#667eea15' : 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  });

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
          Configuración
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Administra tu cuenta y preferencias de la aplicación
        </p>
      </div>

      <div style={tabsStyle}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={tabStyle(activeTab === tab.id)}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {activeTab === 'profile' && <ProfileSection />}
        {activeTab === 'appearance' && <ThemeSection />}
        {activeTab === 'notifications' && <NotificationsSection />}
        {activeTab === 'kanban' && <KanbanStatusesSection />}
        {activeTab === 'transitions' && <TransitionRulesEditor />}
        {activeTab === 'clients' && <ClientAccessSection />}
        {activeTab === 'members' && <MembersSection />}
        {activeTab === 'roles' && <RolesSection />}
      </div>
    </div>
  );
}

SettingsPage.propTypes = {};
